import Debug from 'debug'
import { saveQuestionState, loadQuestionState, QuestionStateInput,
         QuestionPhase,
         QuestionStateMachineData, defaultQuestionStateMachineData,
  isQuestionPhaseEvaluated,
  QuestionAnswer,
  QuestionState,
  QuestionEvaluationResult} from '@/app/question/domain/state-machine'
import { mapQuizQuestionToQuestionStateMachineScore } from './create-question-state-machine'

export type QuizSessionQuestionPhase = 'not-attempted' | 'not-evaluated' | 'correct' |
      'incorrect' | 'disabled' | 'partially-correct'

export interface QuizSessionQuestion {
  id: string
  type: string
  minScore: number
  maxScore: number
  question: string
  content: unknown
  contentScore: {
    min: number
    max: number
  }
  feedback: {
    correct: string
    incorrect: string
    tryAgain: string
  }
}

export type QuizQuestionState = QuestionStateMachineData
export type QuizPlayerQuestionAnswer = QuestionAnswer
export type QuizPlayerQuestionState = QuestionState
export type QuizPlayerQuestionEvaluationResult = QuestionEvaluationResult
export type QuizPlayerInputQuestionStates = (QuestionStateInput | null)[]

interface ViewState {
  disabled: boolean
}

const debug = Debug('quiz-questions-state')

export class QuizQuestionsState {
  
  private questions: QuizSessionQuestion[] = []
  private states: QuizQuestionState[] = []
  private viewStates: ViewState[] = []

  constructor(questions: QuizSessionQuestion[], options: { questionsDisabled: boolean }) {
    this.questions = questions
    this.states = questions.map(q => defaultQuestionStateMachineData(q.minScore))
    this.viewStates = questions.map(_ => ({ disabled: options.questionsDisabled }))
  }

  initializeQuestionStates(questionStates: QuizPlayerInputQuestionStates) {

    debug('initialize-question-states', questionStates)
    
    for (let index = 0; index < questionStates.length; index++) {
      const questionState = questionStates[index]
      if (questionState === null) { continue }

      const score = mapQuizQuestionToQuestionStateMachineScore(this.questions[index])
      const loaded = loadQuestionState(questionState, score)
      
      const state = this.states[index]
      state.state = loaded.state
      state.answer = loaded.answer
      state.phase = loaded.phase
      state.evaluation = loaded.evaluation
    }
  }

  enabledQuestion(questionIndex: number) {
    this.viewStates[questionIndex].disabled = false
  }

  disableQuestion(questionIndex: number) {
    this.viewStates[questionIndex].disabled = true
  }

  isQuestionEnabled(questionIndex: number) {
    return !this.viewStates[questionIndex].disabled    
  }

  isEvaluated(questionIndex: number) {
    const state = this.states[questionIndex]
    return isQuestionPhaseEvaluated(state.phase)
  }

  questionForStateMachine(questionIndex: number) {
    return {
      question: this.questions[questionIndex],
      state: this.states[questionIndex]
    }
  }

  question(questionIndex: number) {

    const state = this.states[questionIndex]
    const question = this.questions[questionIndex]
    return {
      question: question,
      state: state.state,
      answer: state.answer,
      evaluation: state.evaluation,
      evaluated: isQuestionPhaseEvaluated(state.phase),
      showSolution: state.phase === 'solution-shown'
    }
  }

  exportState(questionIndex: number) {
    const data = this.states[questionIndex]
    const question = this.questions[questionIndex]
    const questionState = saveQuestionState(data)
    
    return {
      questionIndex: questionIndex,
      type: question.type,
      state: questionState.state,
      answer: questionState.answer,
      phase: questionState.phase,
      result: questionState.result
    }
  }

  get questionTexts() {
    return this.questions.map(x => x.question)
  }

  get questionPhases(): QuizSessionQuestionPhase[] {

    let phases: QuizSessionQuestionPhase[] = []
    for (let index = 0; index < this.states.length; index++) {
      const phase = mapQuestionPhaseToQuizQuestionPhase(this.states[index],
                                                        this.viewStates[index].disabled)
      phases.push(phase)
    }

    return phases

    function mapQuestionPhaseToQuizQuestionPhase(
      state: QuizQuestionState,
      disabled: boolean): QuizSessionQuestionPhase {

      if (disabled) {
        return 'disabled'
      }

      switch (state.phase) {
        case 'not-answered':
          return 'not-attempted'
        case 'answered':
          return 'not-evaluated'
        case 'evaluated':
        case 'solution-shown':
          switch (state.evaluation.result) {
            case 'correct':
              return 'correct'
            case 'partially-correct':
              return 'partially-correct'
            case 'incorrect':
              return 'incorrect'
            case 'not-evaluated':
              throw new Error(`Coding error: invalid state: ${JSON.stringify(state)}`)
          }
      }
    }
  }

  get questionScores() {
    let scores = []
    
    for (let i = 0; i < this.questions.length; i++) {
      const question = this.questions[i]

      const state = this.states[i]
      const score =  state.evaluation.result !== 'not-evaluated' ?
              state.evaluation.details.score : question.minScore
      scores.push({
        min: question.minScore,
        max: question.maxScore,
        raw: score
      })
    }

    return scores
  }

  get questionCount() {
    return this.questions.length
  }

  notAnsweredQuestionCount(options: { markNotEvaluatedAsNonAnswered: boolean  }) {
    const notAnsweredPhases: QuestionPhase[] = [ 'not-answered' ]
    let count = 0
    for (let state of this.states) {
      if (notAnsweredPhases.indexOf(state.phase) >= 0 ||
            (options.markNotEvaluatedAsNonAnswered && state.phase === 'answered')) {
        count++
      }
    }

    return count
  }
}
